import React, { useState, useRef } from 'react';

import { ClickAwayListener, Popper, Grow, Paper, MenuList, MenuItem } from '@mui/material';
import { withStyles } from '@mui/styles';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';

import Button from 'components/Button';
import MultiExportButtonMenuItem from 'components/buttons/components/MultiExportButtonMenuItem';
import {
  selectActiveFilters,
  selectSearchText,
} from 'components/InfiniteTableSelfStandingV3/selectors';
import ArrowLeft from 'components/SvgComponents/icons/ArrowLeft';
import ChevronDown from 'components/SvgComponents/icons/ChevronDown';
import combineStyles from 'theme/combineStyles';
import tableActionsStyles from 'theme/table-actions-theme';
import { FONTSIZE_S } from 'theme/typography';
import globalMessages from 'translations/messages/global-messages';
import { handleToggleDialog } from 'utils/dialog';
import { cache } from 'utils/hooks';
import localStorageUser from 'utils/localStorageUser';

export const styles = theme => ({
  tooltip: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.text.dark,
    borderRadius: 8,
    color: theme.palette.common.white,
    fontSize: FONTSIZE_S,
    padding: '12px 16px',
    gap: '0.5rem',
  },
  icon: {
    transition: 'transform 250ms',
  },
  iconOpen: {
    transform: 'rotate(180deg)',
  },
  '@media (prefers-reduced-motion: reduce)': {
    icon: {
      transition: 'transform 0ms',
    },
  },
  popper: {
    zIndex: 999,
  },
  displayNone: {
    display: 'none',
  },
});

export const MultiExportButton = ({
  classes,
  intl,
  searchTerm,
  activeFilters,
  checkedItemsProperties = {
    ids: [],
    label: '',
  },
  exportItems,
  customFilters = {},
}) => {
  const [isPopperOpen, setIsPopperOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState(null);

  const buttonRef = useRef(null);

  const handleOnClick = (onExport, menu) => {
    if (!isEmpty(menu.children)) {
      setSelectedMenu(menu);
      return;
    }

    const locale = localStorageUser.getLanguageUserFromLocalStorage();
    const filters = {};
    Object.keys(activeFilters).forEach(key => {
      filters[key] = Array.isArray(activeFilters[key])
        ? activeFilters[key].map(({ id }) => id)
        : activeFilters[key];
    });

    const { ids, label } = checkedItemsProperties;

    const selectedIds = ids?.length ? { [label]: ids } : null;

    onExport({
      locale,
      search_term: searchTerm,
      ...filters,
      ...customFilters,
      ...selectedIds,
    });
  };

  const mapMenuItems = items =>
    items.map(menu => {
      const { label, icon, onExport, tag, children } = menu;
      return (
        <MultiExportButtonMenuItem
          key={label}
          label={label}
          icon={icon}
          tag={tag}
          hasChildren={!isEmpty(children)}
          onClick={() => handleOnClick(onExport, menu)}
        />
      );
    });

  const onClickAway = _ => {
    setIsPopperOpen(!isPopperOpen);
    setSelectedMenu(null);
  };

  return (
    <>
      <Button
        ref={buttonRef}
        aria-owns={isPopperOpen ? 'export-list' : undefined}
        aria-haspopup="true"
        variant="outlined"
        onClick={handleToggleDialog(setIsPopperOpen, isPopperOpen)}
        endIcon={
          <ChevronDown
            className={classNames(classes.icon, {
              [classes.iconOpen]: isPopperOpen,
            })}
          />
        }
      >
        {intl.formatMessage(globalMessages.export)}
      </Button>

      <Popper
        open={isPopperOpen}
        anchorEl={buttonRef.current}
        transition
        disablePortal
        className={classes.popper}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
        ]}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} id="export-list">
            <Paper>
              <ClickAwayListener onClickAway={onClickAway}>
                <MenuList disablePadding>
                  {!isEmpty(selectedMenu) ? (
                    <>
                      <MenuItem
                        className={classNames(classes.menuItem, classes.menuHeader)}
                        onClick={cache([setSelectedMenu], [null])}
                      >
                        <ArrowLeft className={classes.menuIconLeft} width={16} height={16} />
                        {selectedMenu.label}
                      </MenuItem>
                      {mapMenuItems(selectedMenu.children)}
                    </>
                  ) : (
                    mapMenuItems(exportItems)
                  )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

MultiExportButton.propTypes = {
  classes: PropTypes.object.isRequired,
  intl: PropTypes.object,
  searchTerm: PropTypes.string,
  activeFilters: PropTypes.object.isRequired,
  customFilters: PropTypes.object,
  exportItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
      onExport: PropTypes.func,
    }).isRequired,
  ),
  checkedItemsProperties: PropTypes.shape({
    ids: PropTypes.array.isRequired,
    label: PropTypes.string.isRequired,
  }),
};

const mapStateToProps = createStructuredSelector({
  searchTerm: selectSearchText(),
  activeFilters: selectActiveFilters(),
});

const withConnect = connect(
  mapStateToProps,
  null,
);

export default compose(
  withStyles(combineStyles(styles, tableActionsStyles)),
  injectIntl,
  withConnect,
)(MultiExportButton);
